'use client';

import { useEffect, useState } from 'react';

import { apiGet } from '@/lib/api';
import { authModel } from '@/models/authModel';
import type { RegisteredEntryPayment } from '@/app/[locale]/event/[eventId]/libs/registeredEntryState';

export type RegisteredGroup = {
  id: string;
  name: string;
  /** Team-division fields (additive) — present only when the viewer holds a team division. */
  team_name?: string | null;
  team_info?: string | null;
  /** `active` / `withdrawn` (survey follow-up 11). Absent from an older server: active. */
  status?: string | null;
  /** The entry's payment with the organiser figures (follow-up 9); null = no payment recorded. */
  payment?: RegisteredEntryPayment | null;
};

/**
 * The entered player's membership coverage of the event (Members phase 4b, §6.3): the gate's verdict
 * for their first day, `null` when the gate does not apply. `renewal_offer` names the club, type and
 * season the Renew link opens preselected.
 */
export type EntryMembership = {
  status: 'eligible' | 'renewal_pending' | 'ineligible';
  reason: string | null;
  type_name: string | null;
  covers_until?: string | null;
  ends_on?: string | null;
  grace_until?: string | null;
  renews_on?: string | null;
  renewal_offer?: {
    member_type_id: string;
    club_id: string;
    valid_from: string;
  } | null;
};

export type RegistrationStatus = {
  /** True once the session check (and, for authed users, the status fetch) settled. */
  checked: boolean;
  isAuthenticated: boolean;
  isRegistered: boolean;
  registeredGroups: Array<RegisteredGroup>;
  canAddMore: boolean;
  /** The event currency the entries' money is in. */
  currency: string;
  /** Members phase 4b: coverage of the event for the entered player; null = no line to show. */
  membership: EntryMembership | null;
};

const UNRESOLVED: RegistrationStatus = {
  checked: false,
  isAuthenticated: false,
  isRegistered: false,
  registeredGroups: [],
  canAddMore: false,
  currency: 'AUD',
  membership: null,
};

// One resolution per event per page view: the summary card and the mobile
// sticky bar mount together and would otherwise each run the same session
// check + GET. The promise is evicted once settled, so a remount after
// client-side navigation (e.g. returning from the register flow) refetches
// fresh state instead of reading a stale result.
const inflight = new Map<string, Promise<RegistrationStatus>>();

async function resolveStatus(eventId: string): Promise<RegistrationStatus> {
  let isAuthenticated = false;
  try {
    const session = await authModel.getSession();
    isAuthenticated = !!session?.user;
  } catch {
    // treat as guest
  }
  if (!isAuthenticated) {
    return { ...UNRESOLVED, checked: true };
  }
  try {
    const response = await apiGet<{
      success: boolean;
      isRegistered: boolean;
      registeredGroups?: Array<RegisteredGroup>;
      canAddMore?: boolean;
      currency?: string;
      membership?: EntryMembership | null;
    }>(`/api/event/${eventId}/participant-register?accepts_membership_line=1`);
    return {
      checked: true,
      isAuthenticated: true,
      isRegistered: Boolean(response.success && response.isRegistered),
      registeredGroups: response.registeredGroups ?? [],
      canAddMore: Boolean(response.canAddMore),
      currency: response.currency ?? 'AUD',
      membership: response.membership ?? null,
    };
  } catch {
    // No player profile / transient error — treat as not registered; the
    // register page handles those states itself.
    return { ...UNRESOLVED, checked: true, isAuthenticated: true };
  }
}

export function useRegistrationStatus(eventId: string): RegistrationStatus {
  const [status, setStatus] = useState<RegistrationStatus>(UNRESOLVED);

  useEffect(() => {
    let cancelled = false;
    let promise = inflight.get(eventId);
    if (!promise) {
      promise = resolveStatus(eventId).finally(() => {
        inflight.delete(eventId);
      });
      inflight.set(eventId, promise);
    }
    promise.then((resolved) => {
      if (!cancelled) setStatus(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return status;
}
